import { Controller } from "@hotwired/stimulus"

// Auto-populate a website-name (slug) field from a git remote URL as the user
// types it, mirroring the server's git.slug_from_remote_url + suggest_slug cap.
// Stops auto-filling once the user edits the name field by hand, so manual input
// is never clobbered.
//
// Markup (combine with availability-check on the same element):
//   <form data-controller="availability-check slug-from-url">
//     <input data-slug-from-url-target="source">
//     <input data-slug-from-url-target="slug"
//            data-availability-check-target="input">
//   </form>
export default class extends Controller {
  static targets = ["source", "slug"]

  connect() {
    if (!this.hasSourceTarget || !this.hasSlugTarget) return
    // Treat a pre-filled name as user-owned so we don't overwrite it.
    this.manual = this.slugTarget.value.trim() !== ""
    this.boundOnSource = () => this.onSource()
    this.boundOnSlug = (e) => this.onSlug(e)
    this.sourceTarget.addEventListener("input", this.boundOnSource)
    this.slugTarget.addEventListener("input", this.boundOnSlug)
  }

  disconnect() {
    if (this.boundOnSource) this.sourceTarget.removeEventListener("input", this.boundOnSource)
    if (this.boundOnSlug) this.slugTarget.removeEventListener("input", this.boundOnSlug)
  }

  onSource() {
    if (this.manual) return
    const derived = this.deriveSlug(this.sourceTarget.value)
    if (derived === this.slugTarget.value) return
    this.slugTarget.value = derived
    // Let availability-check (and any other listeners) re-run.
    this.slugTarget.dispatchEvent(new Event("input", { bubbles: true }))
  }

  onSlug(event) {
    // Only genuine user keystrokes (isTrusted) flip manual mode; our own
    // programmatic dispatch from onSource() has isTrusted === false.
    if (!event.isTrusted) return
    this.manual = this.slugTarget.value.trim() !== ""
  }

  // Mirror of app.sites.git.slug_from_remote_url + the 18-char cap from
  // Site.suggest_slug. Uniqueness / random suffix is left to the server and the
  // availability-check controller's "taken" feedback.
  deriveSlug(url) {
    url = (url || "").trim()
    if (!url) return ""
    const segments = url.split(/[/:]/).filter(Boolean)
    if (!segments.length) return ""
    let name = segments[segments.length - 1]
    if (name.endsWith(".git")) name = name.slice(0, -4)
    name = name.toLowerCase().replace(/[^a-z0-9_-]/g, "-").replace(/^-+|-+$/g, "")
    return name.slice(0, 18)
  }
}
